import type ChargebeeAddon from 'modules/upsell/lib/selfUpsell/calculator/ChargebeeAddon'
import { PeriodUnit } from 'modules/upsell/lib/selfUpsell/types'

export type PricingType = {
  [Key in PeriodUnit]: {
    perSeat?: ChargebeeAddon
    fee?: ChargebeeAddon
    tiered?: ChargebeeAddon
    stairstep?: ChargebeeAddon
  }
}

export class Pricing {
  private _discount = 0
  constructor(
    private month: PricingType['month'],
    private year: PricingType['year'],
    private six_months: PricingType['six_months']
  ) {}

  get currency() {
    return (
      this.month.perSeat?.currency || this.month.fee?.currency || this.month.stairstep?.currency
    )
  }

  isPerSeat(period: PeriodUnit) {
    const periodPricing = this[period]
    return !!periodPricing.perSeat || !periodPricing.fee || periodPricing.stairstep
  }

  getPlanId(period: PeriodUnit) {
    const periodPricing = this[period]

    return (
      periodPricing.perSeat?._id ||
      periodPricing.fee?._id ||
      periodPricing.stairstep?._id ||
      periodPricing.tiered?._id
    )
  }

  getAllPlanIds(period: PeriodUnit) {
    const periodPricing = this[period]

    const planIds = []

    if (periodPricing.perSeat?._id) {
      planIds.push(periodPricing.perSeat._id)
    }
    if (periodPricing.fee?._id) {
      planIds.push(periodPricing.fee._id)
    }
    if (periodPricing.stairstep?._id) {
      planIds.push(periodPricing.stairstep._id)
    }
    if (periodPricing.tiered?._id) {
      planIds.push(periodPricing.tiered._id)
    }

    return planIds
  }

  setDiscount(discount: number) {
    this._discount = discount
  }

  getSeatsPrice(period: PeriodUnit) {
    const periodPricing = this[period]
    return periodPricing.perSeat?.getMonthlyPrice()
  }

  getOriginalSeatsPrice(period: PeriodUnit) {
    const periodPricing = this[period]
    return periodPricing.perSeat?.getOriginalMonthlyPrice()
  }

  getOriginalFeePrice(period: PeriodUnit) {
    const periodPricing = this[period]
    return periodPricing.fee?.getOriginalMonthlyPrice() ?? undefined
  }

  getFeePrice(period: PeriodUnit) {
    const periodPricing = this[period]
    return periodPricing.fee?.getMonthlyPrice() || 0
  }

  getAnnualFeePrice(period: PeriodUnit) {
    const periodPricing = this[period]
    return periodPricing.fee?.getPrice() || 0
  }

  getTieredPrice(period: PeriodUnit, seats: number) {
    const periodPricing = this[period]

    return {
      tiers: periodPricing.tiered?.tiers,
      selectedPrice: periodPricing.tiered?.getPrice(seats) || 0,
    }
  }

  getStairstepPrice(period: PeriodUnit, seats: number) {
    const periodPricing = this[period]
    return periodPricing.stairstep?.getMonthlyPrice(seats) || 0
  }

  getTotalPrice(period: PeriodUnit, seats: number) {
    const periodPricing = this[period]

    const seatsPrice = periodPricing.perSeat?.getPrice(seats) || 0
    const feePrice = periodPricing.fee?.getPrice() || 0
    const tieredPrice = periodPricing.tiered?.getPrice(seats) || 0
    const stairstepPrice = periodPricing.stairstep?.getPrice(seats) || 0
    const discount = this._discount

    return seatsPrice + feePrice + tieredPrice + stairstepPrice - discount
  }

  getTotalMonthlyPrice(period: PeriodUnit, seats: number) {
    const periodPricing = this[period]

    const seatsPrice = periodPricing.perSeat?.getMonthlyPrice(seats) || 0
    const feePrice = periodPricing.fee?.getMonthlyPrice() || 0
    const tieredPrice = periodPricing.tiered?.getMonthlyPrice() || 0
    const stairstepPrice = periodPricing.stairstep?.getMonthlyPrice(seats) || 0
    const discount = this._discount

    return seatsPrice + feePrice + tieredPrice + stairstepPrice - discount
  }
}
